import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiBriefcase,
  FiEdit2,
  FiFileText,
  FiFolder,
  FiPlus,
  FiStar,
  FiUser,
  FiX,
} from "react-icons/fi";
import AxiosInstance from "../Home/AxiosInstance";
import TraineeHeader from "./TraineeHeader";
import Footers from "../Home/Footers";
import "./traineePortal.css";
import "./TraineeProfile.css";
import "./TraineeCareerPreference.css";

const WORK_MODES = ["Remote", "Hybrid", "Onsite"];
const FLEXIBLE_SHIFTS = ["Night shift", "Day shift", "Both"];
const LOOKING_FOR = ["Jobs", "Internships", "Trainings"];
const PROFICIENCY_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];
const RESUME_ACCEPT =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function joinCsv(values) {
  return (values || []).map((v) => String(v).trim()).filter(Boolean).join(", ");
}

function resumeFileName(path) {
  if (!path) return "Resume";
  const parts = String(path).replace(/\\/g, "/").split("/");
  return parts[parts.length - 1] || "Resume";
}

const EMPTY_PROJECT = {
  project_name: "",
  description: "",
  technologies: "",
  project_url: "",
  role: "",
  start_date: "",
  end_date: "",
};

export default function TraineeCareerPreference() {
  const navigate = useNavigate();
  const traineeId = localStorage.getItem("traineeId");
  const [departments, setDepartments] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [traineeSkills, setTraineeSkills] = useState([]);
  const [showSkillForm, setShowSkillForm] = useState(false);
  const [savingSkill, setSavingSkill] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [skillForm, setSkillForm] = useState({ skill_id: "", proficiency_level: "" });
  const [resumes, setResumes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resumeTitle, setResumeTitle] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [showResumeForm, setShowResumeForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasPreference, setHasPreference] = useState(false);
  const [savedForm, setSavedForm] = useState(null);
  const [form, setForm] = useState({
    summary: "",
    preferred_locations: "",
    preferred_roles: "",
    preferred_departments: [],
    preferred_work_modes: [],
    flexible_shift: "",
    expected_salary: "",
    experience_years: "",
    looking_for: ["Jobs", "Internships", "Trainings"],
  });

  const loadResumes = useCallback(() => {
    if (!traineeId) return Promise.resolve();
    return AxiosInstance.get(`companies/career-preference/${traineeId}/resumes/`)
      .then((res) => setResumes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setResumes([]));
  }, [traineeId]);

  const loadProjects = useCallback(() => {
    if (!traineeId) return Promise.resolve();
    return AxiosInstance.get(`companies/career-preference/${traineeId}/projects/`)
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : []))
      .catch(() => setProjects([]));
  }, [traineeId]);

  useEffect(() => {
    if (!traineeId) {
      setError("Please sign in again.");
      setLoading(false);
      return;
    }

    Promise.all([
      AxiosInstance.get("companies/departments/").catch(() => ({ data: [] })),
      AxiosInstance.get("companies/skills/").catch(() => ({ data: [] })),
      AxiosInstance.get(`companies/career-preference/${traineeId}/`).catch(
        (err) => {
          if (err.response?.status === 404) return { data: null };
          throw err;
        }
      ),
      AxiosInstance.get(`companies/skill/${traineeId}`).catch(() => ({ data: [] })),
      loadResumes(),
      loadProjects(),
    ])
      .then(([deptRes, skillRes, prefRes, traineeSkillRes]) => {
        setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
        setSkillOptions(Array.isArray(skillRes.data) ? skillRes.data : []);
        setTraineeSkills(Array.isArray(traineeSkillRes.data) ? traineeSkillRes.data : []);
        const pref = prefRes.data;
        if (pref && pref.preference_id) {
          const loadedForm = {
            summary: pref.summary || "",
            preferred_locations: pref.preferred_locations || "",
            preferred_roles: pref.preferred_roles || "",
            preferred_departments: splitCsv(pref.preferred_departments),
            preferred_work_modes: splitCsv(pref.preferred_work_modes),
            flexible_shift: pref.flexible_shift || "",
            expected_salary: pref.expected_salary || "",
            experience_years:
              pref.experience_years != null ? String(pref.experience_years) : "",
            looking_for: splitCsv(pref.looking_for).length
              ? splitCsv(pref.looking_for)
              : ["Jobs", "Internships", "Trainings"],
          };
          setForm(loadedForm);
          setSavedForm(loadedForm);
          setHasPreference(true);
          if (Array.isArray(pref.resumes)) {
            setResumes(pref.resumes);
          }
          if (Array.isArray(pref.projects)) {
            setProjects(pref.projects);
          }
        } else {
          setIsEditing(true);
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load career preference.");
      })
      .finally(() => setLoading(false));
  }, [traineeId, loadResumes, loadProjects]);

  const toggleMulti = (key, value) => {
    setForm((prev) => {
      const current = prev[key] || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
    setError("");
    setSuccess("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const handleUploadResume = async (e) => {
    e.preventDefault();
    if (!traineeId || !resumeFile) {
      setError("Choose a resume file to upload.");
      return;
    }
    setUploading(true);
    setError("");
    setSuccess("");
    try {
      const data = new FormData();
      data.append("resume_path", resumeFile);
      if (resumeTitle.trim()) data.append("title", resumeTitle.trim());
      await AxiosInstance.post(
        `companies/career-preference/${traineeId}/resumes/upload/`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      closeResumeForm();
      await loadResumes();
      setSuccess("Resume uploaded.");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error || "Failed to upload resume. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefault = async (resumeId) => {
    try {
      await AxiosInstance.put(
        `companies/career-preference/resume/${resumeId}/`,
        { is_default: "Yes" }
      );
      await loadResumes();
    } catch (err) {
      console.error(err);
      setError("Failed to set default resume.");
    }
  };

  const handleDeleteResume = async (resumeId) => {
    if (!window.confirm("Remove this resume?")) return;
    try {
      await AxiosInstance.delete(
        `companies/career-preference/resume/${resumeId}/delete/`
      );
      await loadResumes();
    } catch (err) {
      console.error(err);
      setError("Failed to delete resume.");
    }
  };

  const closeResumeForm = () => {
    setShowResumeForm(false);
    setResumeTitle("");
    setResumeFile(null);
  };

  const resetProjectForm = () => {
    setEditingProjectId(null);
    setShowProjectForm(false);
    setProjectForm(EMPTY_PROJECT);
  };

  const startAddProject = () => {
    setEditingProjectId(null);
    setProjectForm(EMPTY_PROJECT);
    setShowProjectForm(true);
  };

  const startEditProject = (item) => {
    setEditingProjectId(item.project_id);
    setShowProjectForm(true);
    setProjectForm({
      project_name: item.project_name || "",
      description: item.description || "",
      technologies: item.technologies || "",
      project_url: item.project_url || "",
      role: item.role || "",
      start_date: item.start_date || "",
      end_date: item.end_date || "",
    });
  };

  const updateProjectField = (field) => (e) =>
    setProjectForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSaveProject = async (e) => {
    e.preventDefault();
    if (!traineeId) return;
    if (!projectForm.project_name.trim()) {
      setError("Project name is required.");
      return;
    }
    setSavingProject(true);
    setError("");
    setSuccess("");
    const payload = {
      project_name: projectForm.project_name.trim(),
      description: projectForm.description.trim() || null,
      technologies: projectForm.technologies.trim() || null,
      project_url: projectForm.project_url.trim() || null,
      role: projectForm.role.trim() || null,
      start_date: projectForm.start_date || null,
      end_date: projectForm.end_date || null,
    };
    try {
      if (editingProjectId) {
        await AxiosInstance.put(
          `companies/career-preference/project/${editingProjectId}/`,
          payload
        );
        setSuccess("Project updated.");
      } else {
        await AxiosInstance.post(
          `companies/career-preference/${traineeId}/projects/create/`,
          payload
        );
        setSuccess("Project added.");
      }
      resetProjectForm();
      await loadProjects();
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error || "Failed to save project. Please try again."
      );
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Remove this project?")) return;
    try {
      await AxiosInstance.delete(
        `companies/career-preference/project/${projectId}/delete/`
      );
      if (editingProjectId === projectId) resetProjectForm();
      await loadProjects();
    } catch (err) {
      console.error(err);
      setError("Failed to delete project.");
    }
  };

  const resetSkillForm = () => {
    setShowSkillForm(false);
    setEditingSkillId(null);
    setSkillForm({ skill_id: "", proficiency_level: "" });
  };

  const startAddSkill = () => {
    setEditingSkillId(null);
    setSkillForm({ skill_id: "", proficiency_level: "" });
    setShowSkillForm(true);
  };

  const startEditSkill = (item) => {
    setEditingSkillId(item.trainee_skill_id);
    setSkillForm({
      skill_id: String(item.skill?.skill_id ?? item.skill ?? ""),
      proficiency_level: item.proficiency_level || "",
    });
    setShowSkillForm(true);
  };

  const handleSaveSkill = async () => {
    if (!skillForm.skill_id || !skillForm.proficiency_level) {
      setError("Skill and proficiency level are required.");
      return;
    }
    setSavingSkill(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        trainee: traineeId,
        skill: skillForm.skill_id,
        proficiency_level: skillForm.proficiency_level,
      };
      if (editingSkillId) {
        await AxiosInstance.put(`companies/skill/${editingSkillId}/`, payload);
      } else {
        await AxiosInstance.post("companies/skill", payload);
      }
      const res = await AxiosInstance.get(`companies/skill/${traineeId}`);
      setTraineeSkills(Array.isArray(res.data) ? res.data : []);
      setSuccess(editingSkillId ? "Skill updated." : "Skill added.");
      resetSkillForm();
    } catch (err) {
      console.error(err);
      setError("Failed to save skill.");
    } finally {
      setSavingSkill(false);
    }
  };

  const handleDeleteSkill = async (item) => {
    if (!window.confirm("Are you sure you want to remove this skill?")) return;
    try {
      await AxiosInstance.delete(
        `companies/trainee-skill/${item.trainee_skill_id}`
      );
      setTraineeSkills((prev) =>
        prev.filter((row) => row.trainee_skill_id !== item.trainee_skill_id)
      );
      if (editingSkillId === item.trainee_skill_id) resetSkillForm();
    } catch (err) {
      console.error(err);
      setError("Failed to delete skill.");
    }
  };

  const addDepartment = (name) => {
    if (!name) return;
    setForm((prev) => {
      if (prev.preferred_departments.includes(name)) return prev;
      return {
        ...prev,
        preferred_departments: [...prev.preferred_departments, name],
      };
    });
    setError("");
    setSuccess("");
  };

  const removeDepartment = (name) => {
    setForm((prev) => ({
      ...prev,
      preferred_departments: prev.preferred_departments.filter(
        (item) => item !== name
      ),
    }));
  };

  const selectedSkillIds = new Set(
    traineeSkills.map((item) => String(item.skill?.skill_id ?? item.skill ?? ""))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!traineeId) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        summary: form.summary.trim(),
        preferred_locations: form.preferred_locations.trim(),
        preferred_roles: form.preferred_roles.trim(),
        preferred_departments: joinCsv(form.preferred_departments),
        preferred_work_modes: joinCsv(form.preferred_work_modes),
        flexible_shift: form.flexible_shift || null,
        expected_salary: form.expected_salary.trim(),
        experience_years: form.experience_years
          ? Number(form.experience_years)
          : null,
        looking_for: joinCsv(form.looking_for),
      };
      await AxiosInstance.post(
        `companies/career-preference/${traineeId}/upsert/`,
        payload
      );
      setSavedForm(form);
      setHasPreference(true);
      setIsEditing(false);
      setSuccess("Career preference saved. Matches will appear on your dashboard.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
          "Failed to save career preference. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setError("");
    setSuccess("");
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEditing = () => {
    if (!hasPreference) {
      navigate(-1);
      return;
    }
    if (savedForm) setForm(savedForm);
    resetSkillForm();
    setError("");
    setSuccess("");
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPageHead = () => (
    <div className="cp-page-head">
      <div className="cp-title-row">
        <button
          type="button"
          className="cp-back-icon"
          onClick={isEditing && hasPreference ? cancelEditing : () => navigate(-1)}
          aria-label="Back"
        >
          <FiArrowLeft />
        </button>
        <h1>{isEditing && hasPreference ? "Edit career preference" : "Career preference"}</h1>
        {!isEditing ? (
          <button
            type="button"
            className="cp-edit-icon"
            onClick={startEditing}
            aria-label="Edit career preference"
            title="Edit"
          >
            <FiEdit2 />
          </button>
        ) : null}
      </div>
      {isEditing ? (
        <p>
          Tell us what you are looking for — we will match jobs, internships,
          and trainings on your dashboard. You can store multiple resumes here.
        </p>
      ) : null}
    </div>
  );

  const renderValue = (value) =>
    value ? value : <span className="cp-view-empty">Not added</span>;

  const renderTags = (items) =>
    items && items.length ? (
      <div className="cp-chips">
        {items.map((item) => (
          <span key={item} className="cp-view-tag">
            {item}
          </span>
        ))}
      </div>
    ) : (
      <span className="cp-view-empty">Not added</span>
    );

  const renderView = () => (
    <>
      {success ? <div className="alert alert-success">{success}</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      <section className="tp-panel">
        {renderPageHead()}

        <div className="tp-panel-head cp-panel-head">
          <span className="cp-panel-icon">
            <FiUser />
          </span>
          <div>
            <h2>About you</h2>
          </div>
        </div>
        <dl className="cp-view-grid">
          <div className="cp-view-field cp-view-field--full">
            <dt>Professional summary</dt>
            <dd className="cp-view-summary">{renderValue(form.summary)}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Preferred locations</dt>
            <dd>{renderTags(splitCsv(form.preferred_locations))}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Preferred roles</dt>
            <dd>{renderTags(splitCsv(form.preferred_roles))}</dd>
          </div>
          <div className="cp-view-field cp-view-field--full">
            <dt>Preferred departments</dt>
            <dd>{renderTags(form.preferred_departments)}</dd>
          </div>
        </dl>
      </section>

      <section className="tp-panel">
        <div className="tp-panel-head cp-panel-head">
          <span className="cp-panel-icon">
            <FiBriefcase />
          </span>
          <div>
            <h2>Work preferences</h2>
          </div>
        </div>
        <dl className="cp-view-grid">
          <div className="cp-view-field">
            <dt>Preferred work modes</dt>
            <dd>{renderTags(form.preferred_work_modes)}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Flexible shift</dt>
            <dd>{renderValue(form.flexible_shift)}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Looking for</dt>
            <dd>{renderTags(form.looking_for)}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Expected salary</dt>
            <dd>{renderValue(form.expected_salary)}</dd>
          </div>
          <div className="cp-view-field">
            <dt>Experience</dt>
            <dd>
              {renderValue(
                form.experience_years ? `${form.experience_years} years` : ""
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="tp-panel">
        <div className="tp-panel-head cp-panel-head">
          <span className="cp-panel-icon">
            <FiStar />
          </span>
          <div>
            <h2>Skills</h2>
          </div>
        </div>
        {traineeSkills.length === 0 ? (
          <p className="cp-empty">No skills added yet.</p>
        ) : (
          <div className="cp-chips">
            {traineeSkills.map((item) => (
              <span key={item.trainee_skill_id} className="cp-view-tag">
                {item.skill?.skill_name || item.skill_name || "Skill"}
                {item.proficiency_level ? (
                  <small> · {item.proficiency_level}</small>
                ) : null}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="tp-panel">
        <div className="tp-panel-head cp-panel-head">
          <span className="cp-panel-icon">
            <FiFileText />
          </span>
          <div>
            <h2>Resumes</h2>
          </div>
        </div>
        {resumes.length === 0 ? (
          <p className="cp-empty">No resumes uploaded yet.</p>
        ) : (
          <ul className="cp-list">
            {resumes.map((item) => {
              const url = item.resume_path
                ? `${AxiosInstance.defaults.baseURL}media/${item.resume_path}`
                : null;
              const isDefault =
                String(item.is_default || "").toLowerCase() === "yes";
              return (
                <li key={item.resume_id} className="cp-item">
                  <div className="cp-item-main cp-item-main--stack">
                    <div className="cp-item-title">
                      <strong>{item.title || resumeFileName(item.resume_path)}</strong>
                      {isDefault ? <span className="tp-badge ok">Default</span> : null}
                    </div>
                    <span className="cp-item-sub">
                      {resumeFileName(item.resume_path)}
                    </span>
                  </div>
                  {url ? (
                    <div className="cp-item-actions">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cp-action"
                      >
                        View
                      </a>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="tp-panel">
        <div className="tp-panel-head cp-panel-head">
          <span className="cp-panel-icon">
            <FiFolder />
          </span>
          <div>
            <h2>Projects</h2>
          </div>
        </div>
        {projects.length === 0 ? (
          <p className="cp-empty">No projects added yet.</p>
        ) : (
          <ul className="cp-list">
            {projects.map((item) => (
              <li key={item.project_id} className="cp-item">
                <div className="cp-item-main cp-item-main--stack">
                  <div className="cp-item-title">
                    <strong>{item.project_name}</strong>
                    {item.role ? <span className="tp-badge">{item.role}</span> : null}
                  </div>
                  {item.technologies ? (
                    <span className="cp-item-sub">{item.technologies}</span>
                  ) : null}
                  {item.description ? (
                    <p className="cp-item-desc">{item.description}</p>
                  ) : null}
                  {item.project_url ? (
                    <a
                      href={item.project_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tp-text-link cp-item-link"
                    >
                      {item.project_url}
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );

  const renderChips = (options, isActive, onToggle) => (
    <div className="cp-chips">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`cp-chip ${isActive(option) ? "active" : ""}`}
          onClick={() => onToggle(option)}
          disabled={saving}
        >
          {option}
        </button>
      ))}
    </div>
  );

  return (
    <div className="TraineePage als-trainee-dash als-trainee-page cp-page">
      <TraineeHeader />

      <main className="tp-body cp-body">
        {loading ? (
          <section className="tp-panel">
            <p className="text-muted mb-0">Loading career preference…</p>
          </section>
        ) : !isEditing ? (
          renderView()
        ) : (
          <form onSubmit={handleSubmit}>
            <section className="tp-panel">
              {renderPageHead()}

              <div className="tp-panel-head cp-panel-head">
                <span className="cp-panel-icon">
                  <FiUser />
                </span>
                <div>
                  <h2>About you</h2>
                  <p>Your background and the roles you want.</p>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Professional summary</label>
                <textarea
                  className="form-control"
                  name="summary"
                  rows={4}
                  placeholder="Short summary of your background and goals"
                  value={form.summary}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Preferred locations</label>
                  <input
                    type="text"
                    className="form-control"
                    name="preferred_locations"
                    placeholder="e.g. Hyderabad, Bangalore, Remote"
                    value={form.preferred_locations}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <small className="cp-hint">Comma-separated cities</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Preferred roles</label>
                  <input
                    type="text"
                    className="form-control"
                    name="preferred_roles"
                    placeholder="e.g. Frontend Developer, Data Analyst"
                    value={form.preferred_roles}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <small className="cp-hint">Comma-separated role titles</small>
                </div>
              </div>

              <div>
                <label className="form-label">Preferred departments</label>
                <select
                  className="form-select"
                  value=""
                  disabled={saving || !departments.length}
                  onChange={(e) => addDepartment(e.target.value)}
                >
                  <option value="">
                    {departments.length
                      ? "Select department"
                      : "No departments available"}
                  </option>
                  {departments
                    .filter(
                      (dept) =>
                        !form.preferred_departments.includes(dept.department_name)
                    )
                    .map((dept) => (
                      <option key={dept.department_id} value={dept.department_name}>
                        {dept.department_name}
                      </option>
                    ))}
                </select>
                {form.preferred_departments.length ? (
                  <div className="cp-chips mt-2">
                    {form.preferred_departments.map((name) => (
                      <span key={name} className="cp-tag">
                        {name}
                        <button
                          type="button"
                          onClick={() => removeDepartment(name)}
                          disabled={saving}
                          aria-label={`Remove ${name}`}
                        >
                          <FiX />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="tp-panel">
              <div className="tp-panel-head tp-panel-head--row">
                <div className="cp-panel-head">
                  <span className="cp-panel-icon">
                    <FiStar />
                  </span>
                  <div>
                    <h2>Skills</h2>
                    <p>Used to match jobs, internships, and trainings.</p>
                  </div>
                </div>
                {!showSkillForm ? (
                  <button
                    type="button"
                    className="tp-btn-outline"
                    onClick={startAddSkill}
                    disabled={savingSkill}
                  >
                    <FiPlus /> Add skill
                  </button>
                ) : null}
              </div>

              {showSkillForm ? (
                <div className="cp-inline-form">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Skill</label>
                      <select
                        className="form-select"
                        value={skillForm.skill_id}
                        disabled={savingSkill}
                        onChange={(e) =>
                          setSkillForm((prev) => ({
                            ...prev,
                            skill_id: e.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {skillOptions.length ? "Select skill" : "No skills available"}
                        </option>
                        {skillOptions
                          .filter((skill) => {
                            const id = String(skill.skill_id);
                            return (
                              !selectedSkillIds.has(id) ||
                              id === String(skillForm.skill_id)
                            );
                          })
                          .map((skill) => (
                            <option key={skill.skill_id} value={skill.skill_id}>
                              {skill.skill_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Proficiency</label>
                      <select
                        className="form-select"
                        value={skillForm.proficiency_level}
                        disabled={savingSkill}
                        onChange={(e) =>
                          setSkillForm((prev) => ({
                            ...prev,
                            proficiency_level: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select proficiency</option>
                        {PROFICIENCY_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="cp-inline-actions">
                    <button
                      type="button"
                      className="tp-btn-outline"
                      onClick={resetSkillForm}
                      disabled={savingSkill}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="tp-btn-primary"
                      onClick={handleSaveSkill}
                      disabled={savingSkill}
                    >
                      {savingSkill
                        ? "Saving…"
                        : editingSkillId
                          ? "Update skill"
                          : "Save skill"}
                    </button>
                  </div>
                </div>
              ) : null}

              {traineeSkills.length === 0 ? (
                <p className="cp-empty">No skills added yet.</p>
              ) : (
                <ul className="cp-list">
                  {traineeSkills.map((item) => (
                    <li key={item.trainee_skill_id} className="cp-item">
                      <div className="cp-item-main">
                        <strong>
                          {item.skill?.skill_name || item.skill_name || "Skill"}
                        </strong>
                        {item.proficiency_level ? (
                          <span className="tp-badge">{item.proficiency_level}</span>
                        ) : null}
                      </div>
                      <div className="cp-item-actions">
                        <button
                          type="button"
                          className="cp-action"
                          onClick={() => startEditSkill(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cp-action cp-action--danger"
                          onClick={() => handleDeleteSkill(item)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="tp-panel">
              <div className="tp-panel-head cp-panel-head">
                <span className="cp-panel-icon">
                  <FiBriefcase />
                </span>
                <div>
                  <h2>Work preferences</h2>
                  <p>How and where you would like to work.</p>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Preferred work modes</label>
                {renderChips(
                  WORK_MODES,
                  (mode) => form.preferred_work_modes.includes(mode),
                  (mode) => toggleMulti("preferred_work_modes", mode)
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Flexible shift</label>
                {renderChips(
                  FLEXIBLE_SHIFTS,
                  (shift) => form.flexible_shift === shift,
                  (shift) =>
                    setForm((prev) => ({
                      ...prev,
                      flexible_shift: prev.flexible_shift === shift ? "" : shift,
                    }))
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Looking for</label>
                {renderChips(
                  LOOKING_FOR,
                  (item) => form.looking_for.includes(item),
                  (item) => toggleMulti("looking_for", item)
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Expected salary</label>
                  <input
                    type="text"
                    className="form-control"
                    name="expected_salary"
                    placeholder="e.g. 6-10 LPA"
                    value={form.expected_salary}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Experience (years)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="experience_years"
                    min="0"
                    step="0.5"
                    placeholder="e.g. 2"
                    value={form.experience_years}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>
              </div>
            </section>

            <section className="tp-panel">
              <div className="tp-panel-head tp-panel-head--row">
                <div className="cp-panel-head">
                  <span className="cp-panel-icon">
                    <FiFileText />
                  </span>
                  <div>
                    <h2>Resumes</h2>
                    <p>
                      Upload PDF, DOC or DOCX files. Mark one as default for quick
                      apply.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="tp-btn-outline"
                  onClick={() => {
                    setResumeTitle("");
                    setResumeFile(null);
                    setShowResumeForm(true);
                  }}
                  disabled={uploading}
                >
                  <FiPlus /> Add resume
                </button>
              </div>

              {resumes.length === 0 ? (
                <p className="cp-empty">No resumes uploaded yet.</p>
              ) : (
                <ul className="cp-list">
                  {resumes.map((item) => {
                    const url = item.resume_path
                      ? `${AxiosInstance.defaults.baseURL}media/${item.resume_path}`
                      : null;
                    const isDefault =
                      String(item.is_default || "").toLowerCase() === "yes";
                    return (
                      <li key={item.resume_id} className="cp-item">
                        <div className="cp-item-main cp-item-main--stack">
                          <div className="cp-item-title">
                            <strong>
                              {item.title || resumeFileName(item.resume_path)}
                            </strong>
                            {isDefault ? (
                              <span className="tp-badge ok">Default</span>
                            ) : null}
                          </div>
                          <span className="cp-item-sub">
                            {resumeFileName(item.resume_path)}
                          </span>
                        </div>
                        <div className="cp-item-actions">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cp-action"
                            >
                              View
                            </a>
                          ) : null}
                          {!isDefault ? (
                            <button
                              type="button"
                              className="cp-action"
                              onClick={() => handleSetDefault(item.resume_id)}
                            >
                              Set default
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="cp-action cp-action--danger"
                            onClick={() => handleDeleteResume(item.resume_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="tp-panel">
              <div className="tp-panel-head tp-panel-head--row">
                <div className="cp-panel-head">
                  <span className="cp-panel-icon">
                    <FiFolder />
                  </span>
                  <div>
                    <h2>Projects</h2>
                    <p>Portfolio projects with technologies, role, and links.</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="tp-btn-outline"
                  onClick={startAddProject}
                  disabled={savingProject}
                >
                  <FiPlus /> Add project
                </button>
              </div>

              {projects.length === 0 ? (
                <p className="cp-empty">No projects added yet.</p>
              ) : (
                <ul className="cp-list">
                  {projects.map((item) => (
                    <li key={item.project_id} className="cp-item">
                      <div className="cp-item-main cp-item-main--stack">
                        <div className="cp-item-title">
                          <strong>{item.project_name}</strong>
                          {item.role ? (
                            <span className="tp-badge">{item.role}</span>
                          ) : null}
                        </div>
                        {item.technologies ? (
                          <span className="cp-item-sub">{item.technologies}</span>
                        ) : null}
                        {item.description ? (
                          <p className="cp-item-desc">{item.description}</p>
                        ) : null}
                        {item.project_url ? (
                          <a
                            href={item.project_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tp-text-link cp-item-link"
                          >
                            {item.project_url}
                          </a>
                        ) : null}
                      </div>
                      <div className="cp-item-actions">
                        <button
                          type="button"
                          className="cp-action"
                          onClick={() => startEditProject(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cp-action cp-action--danger"
                          onClick={() => handleDeleteProject(item.project_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="cp-form-footer">
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {success ? (
                  <div className="alert alert-success">{success}</div>
                ) : null}
                <div className="cp-save-bar">
                  <button
                    type="button"
                    className="tp-btn-outline"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="tp-btn-primary" disabled={saving}>
                    {saving ? "Saving…" : "Save preference"}
                  </button>
                </div>
              </div>
            </section>
          </form>
        )}
      </main>

      <Footers />

      {showResumeForm ? (
        <div className="modal d-block cp-modal" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload resume</h5>
                <button
                  type="button"
                  className="cp-modal-close"
                  aria-label="Close"
                  onClick={closeResumeForm}
                  disabled={uploading}
                >
                  <FiX />
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Resume title</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Frontend CV"
                    value={resumeTitle}
                    onChange={(e) => setResumeTitle(e.target.value)}
                    disabled={uploading}
                  />
                </div>
                <div>
                  <label className="form-label">File (PDF, DOC, DOCX)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept={RESUME_ACCEPT}
                    onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                    disabled={uploading}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="tp-btn-outline"
                  onClick={closeResumeForm}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tp-btn-primary"
                  onClick={handleUploadResume}
                  disabled={uploading || !resumeFile}
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showProjectForm ? (
        <div className="modal d-block cp-modal" tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingProjectId ? "Edit project" : "Add project"}
                </h5>
                <button
                  type="button"
                  className="cp-modal-close"
                  aria-label="Close"
                  onClick={resetProjectForm}
                  disabled={savingProject}
                >
                  <FiX />
                </button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Project name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.project_name}
                      onChange={updateProjectField("project_name")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Your role</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Full-stack developer"
                      value={projectForm.role}
                      onChange={updateProjectField("role")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={projectForm.description}
                      onChange={updateProjectField("description")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Technologies</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="React, Django, MySQL"
                      value={projectForm.technologies}
                      onChange={updateProjectField("technologies")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Project URL</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://"
                      value={projectForm.project_url}
                      onChange={updateProjectField("project_url")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Start date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={projectForm.start_date}
                      onChange={updateProjectField("start_date")}
                      disabled={savingProject}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">End date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={projectForm.end_date}
                      onChange={updateProjectField("end_date")}
                      disabled={savingProject}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="tp-btn-outline"
                  onClick={resetProjectForm}
                  disabled={savingProject}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tp-btn-primary"
                  onClick={handleSaveProject}
                  disabled={savingProject}
                >
                  {savingProject
                    ? "Saving…"
                    : editingProjectId
                      ? "Update project"
                      : "Add project"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
